import { Journey } from '@audibene/ta.utils-opportunity'
import { LoadingButton } from '@mui/lab'
import { Alert, Button, Grid, Skeleton, Typography } from '@mui/material'
import { determineRecommendationIssues, Issues } from 'components/Issues'
import { useConsultantUser } from 'hooks/useConsultantUser'
import { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AvailabilityMode, Country, TeamAccess, teleaudiologyOrTaLightJourneys } from '../../../../constants'
import {
  useAppointmentTypeOptions,
  useAsegoAvailabilityDays,
  useAsegoTimesIntegration,
  useDebug,
  useFeatureFlags,
  useProviderRecommendations,
  useShopIdToIsCabinOnly,
} from '../../../../hooks'
import { useCombinedResourceAvailabilities } from '../../../../hooks/useCombinedResourceAvailabilities'
import { CustomerProfile, TimeZones, toResource } from '../../../../types'
import { appointmentTypeOptions, codeToCountry } from '../../../../util'
import { mergeAvailabilities } from '../../../../util/mergeAvailabilities'
import { mergeAvailabilityResponses } from '../../../../util/mergeAvailabilityResponses'
import { Select, TextArea } from '../../../forms'
import { getReservationAvailabilityWindow } from '../../utils'
import { ReservationFormData } from './constants'
import { Day } from './Day'
import { Time } from './Time'

const timezoneOptions = (timezones: TimeZones): { name: string; value: string }[] => {
  return Object.keys(timezones).map((key) => ({
    name: key,
    value: key,
  }))
}

type ReservationFormProps = {
  customerProfile: CustomerProfile
  ianaTimezones: TimeZones
  journey: Journey
  insuranceCompany: string
  restrictToShopSalesforceId: string
}

const ReservationForm = ({
  customerProfile,
  ianaTimezones,
  journey,
  insuranceCompany,
  restrictToShopSalesforceId,
}: ReservationFormProps) => {
  const timezones = timezoneOptions(ianaTimezones)
  const { t } = useTranslation()
  const debug = useDebug()
  const { setValue, watch, formState, getValues } = useFormContext<ReservationFormData>()
  const customerCountry = codeToCountry(customerProfile.country)
  const { appointmentTypes } = useAppointmentTypeOptions({ country: customerCountry, journey }, { enabled: true })
  const [appointmentType, timezone, dateStart, clientStartDate, state, teamAccess] = watch([
    'appointmentType',
    'timezone',
    'dateStart',
    'clientStartDate',
    'state',
    'teamAccess',
  ] as const)

  useEffect(() => {
    if (!clientStartDate) {
      setValue('clientStartDate', dateStart)
    }
  }, [clientStartDate, setValue])

  const [
    reservationAvailabilityStartDaysOffset,
    reservationAvailabilityWindowInDays,
    isSquadBasedSchedulingEnabled,
    allowChangingTeamAccessFeatureFlag,
    isRecommendationBasedAvailabilitiesEnabled,
  ] = useFeatureFlags<number | boolean | undefined>([
    'RESERVATION_AVAILABILITY_START_DATE_DAYS_OFFSET',
    'RESERVATION_AVAILABILITY_WINDOW_IN_DAYS',
    'ENABLE_SQUAD_BASED_SCHEDULING',
    'ALLOW_CHANGING_TEAM_ACCESS',
    'RECOMMENDATION_BASED_AVAILABILITIES',
  ])

  // Load recommendations

  const { consultant, isLoading: isConsultantLoading } = useConsultantUser()

  const filterBySalesTeamGid =
    isSquadBasedSchedulingEnabled && teamAccess === TeamAccess.MY_TEAM && teleaudiologyOrTaLightJourneys.has(journey)

  const isRecommendationsQueryEnabled = filterBySalesTeamGid ? !!customerProfile && !isConsultantLoading : !!customerProfile

  const {
    recommendations,
    isError: haveProviderRecommendationsErrored,
    isLoading: areProviderRecommendationsLoading,
  } = useProviderRecommendations(
    {
      country: customerProfile?.country as string,
      state: state ?? '',
      insuranceCompany,
      journey: journey as Journey,
      shopSalesforceId: restrictToShopSalesforceId,
      salesTeamGid: filterBySalesTeamGid ? consultant?.salesTeamGid : undefined,
    },
    isRecommendationsQueryEnabled
  )

  const isLoadingResources = areProviderRecommendationsLoading || (filterBySalesTeamGid && isConsultantLoading)

  useEffect(() => {
    if (recommendations?.traceId) {
      setValue('recommendationId', recommendations.traceId)
    }
  }, [recommendations?.traceId, setValue])

  const resources = useMemo(() => {
    return recommendations?.providers?.map((provider) => toResource(provider.resource)) ?? []
  }, [recommendations?.providers])

  // Load availabilities

  const appointmentTypeConfiguration = appointmentTypes ? appointmentTypes[appointmentType] : undefined
  const durationInMinutes = appointmentTypeConfiguration?.duration_in_minutes ?? 0

  const { start: recommendationAvailabilitiesStartDate, end: recommendationAvailabilitiesEndDate } = useMemo(
    () =>
      getReservationAvailabilityWindow({
        reservationAvailabilityStartDaysOffset: reservationAvailabilityStartDaysOffset as number | undefined,
        reservationAvailabilityWindowInDays: reservationAvailabilityWindowInDays as number | undefined,
        timeZone: timezone,
      }),
    [reservationAvailabilityStartDaysOffset, reservationAvailabilityWindowInDays, timezone]
  )

  const isAvailabilitiesQueryEnabled = !isLoadingResources && !!appointmentTypeConfiguration?.duration_in_minutes && !!resources.length

  // Per-shop CABIN_ONLY: multi-shop and restrict_to_shop.
  // When restrict_to_shop is set, resources will come from one shop;
  // We still fetch that shop's config so CABIN_ONLY shops skip fitter pairing.
  const { shopIdToIsCabinOnly, isLoading: isShopIdToIsCabinOnlyLoading } = useShopIdToIsCabinOnly({
    journey: journey,
    resources,
    isEnabled: isAvailabilitiesQueryEnabled,
  })

  const isSingleShopContext = Boolean(restrictToShopSalesforceId)

  const availabilityParams = {
    appointmentType,
    dateEnd: recommendationAvailabilitiesEndDate,
    dateStart: recommendationAvailabilitiesStartDate,
    durationInMinutes,
    isEnabled: isAvailabilitiesQueryEnabled,
    journey: journey,
    mode: isSingleShopContext ? AvailabilityMode.DEFAULT : AvailabilityMode.NON_PARTIAL,
    resources,
    shopIdToIsCabinOnly,
    timeZone: timezone,
    ...(isRecommendationBasedAvailabilitiesEnabled && recommendations?.gid ? { prsRecommendationGid: recommendations?.gid } : {}),
  }

  const {
    data: availabilities,
    isError: haveRecommendedAvailabilitiesErrored,
    isLoading: areRecommendedAvailabilitiesLoading,
    refetch: refetchAvailabilities,
  } = useCombinedResourceAvailabilities(availabilityParams)

  const { data: asegoAvailabilities, isLoading: isAsegoDaysLoading } = useAsegoAvailabilityDays({
    ...availabilityParams,
    isEnabled: availabilityParams.isEnabled && !isSingleShopContext,
  })

  const mergedAvailabilityData = mergeAvailabilityResponses(availabilities, asegoAvailabilities)

  const { mergedData: finalMergedAvailabilityData, isLoading: isAsegoTimesLoading } = useAsegoTimesIntegration({
    ...availabilityParams,
    clientStartDate,
    asegoAvailabilities,
    mergedAvailabilityData,
  })

  const mergedAvailabilities = mergeAvailabilities(finalMergedAvailabilityData)

  if (haveProviderRecommendationsErrored) {
    return <Issues summary={{ 'recommendation.issues': ['recommendation.issues.default'] }} />
  }

  if (!isLoadingResources && !recommendations?.providers?.length) {
    const recommendationIssues = determineRecommendationIssues({
      customerProfileAddress: customerProfile.billingAddress,
      recommendations,
      isReservation: true,
    })

    return (
      <Issues
        summary={
          recommendationIssues?.summary ?? {
            'lead.recommendations.notFound': [],
          }
        }
        summaryMetadata={recommendationIssues?.metaData}
      />
    )
  }

  const isAvailabilitiesLoading =
    isLoadingResources || areRecommendedAvailabilitiesLoading || isAsegoDaysLoading || isShopIdToIsCabinOnlyLoading

  const showTeamAccessDropdown =
    isSquadBasedSchedulingEnabled && allowChangingTeamAccessFeatureFlag && teleaudiologyOrTaLightJourneys.has(journey)

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={4} minHeight="22rem">
        <Grid item xs={12} marginBottom={2}>
          <Typography color="primary" variant="h1">
            {customerProfile.lastName}, {customerProfile.firstName} ({customerProfile.salutation})
          </Typography>
        </Grid>
        <Grid item xs={12} marginBottom={2}>
          <Select disabled label={t('common.label.typeOfAppointment')} name="appointmentType" options={appointmentTypeOptions} />
        </Grid>
        <Grid item xs={12} marginBottom={2}>
          <Select label={t('common.label.customerTimezone')} name="timezone" options={timezones} />
        </Grid>
        {customerCountry === Country.US && (
          <Grid item xs={12}>
            <TextArea disabled fullWidth label={t('common.label.customerState')} name="state" variant="standard" />
          </Grid>
        )}
        {showTeamAccessDropdown && (
          <Grid item xs={12}>
            <Select
              label={t('common.label.teamAccess')}
              name="teamAccess"
              options={[
                { name: t('common.label.teamAccessOptions.myTeam'), value: TeamAccess.MY_TEAM },
                { name: t('common.label.teamAccessOptions.allTeams'), value: TeamAccess.ALL_TEAMS },
              ]}
            />
          </Grid>
        )}
      </Grid>
      {haveRecommendedAvailabilitiesErrored && (
        <Grid item xs={12}>
          <Alert
            action={
              <Button color="inherit" size="small" onClick={() => refetchAvailabilities()}>
                {t('common.label.tryAgain')}
              </Button>
            }
            severity="error"
          >
            {t('common.message.availabilitiesLoadError')}
          </Alert>
        </Grid>
      )}
      <Grid item xs={12} md={4} minHeight="22rem">
        {isAvailabilitiesLoading ? (
          <Skeleton variant="rectangular" width="100%" height="100%" />
        ) : (
          <Day availabilities={mergedAvailabilities} />
        )}
      </Grid>
      <Grid item xs={12} md={4} minHeight="22rem">
        {isAvailabilitiesLoading ? (
          <Skeleton variant="rectangular" width="100%" height="100%" />
        ) : (
          <Time availabilities={mergedAvailabilities} isLoading={isAsegoTimesLoading} />
        )}
      </Grid>
      <Grid item xs={12} textAlign={'end'} minHeight="4rem">
        <LoadingButton loading={isAvailabilitiesLoading || formState.isSubmitting} variant="contained" type="submit">
          {t('common.reserve')}
        </LoadingButton>
      </Grid>
      {debug && <pre>{JSON.stringify(getValues(), null, 2)}</pre>}
    </Grid>
  )
}

export { ReservationForm }
